import {
    Events,
    createItemsViewEvent,
    dataLayerPush,
    trackEEImpression,
} from '@libs/analytics';
import type {
    MultiProductPromotionResponse,
    MultiProductPromotionResponseItem,
    Product,
    ProductPromotion,
} from '@libs/common-next';
import {
    getNextApiRoute,
    isUserEligibleForPromo,
    typedFetch,
} from '@libs/common-next';
import { HttpMethod, type Nullable } from '@libs/core';
import type {
    CurrentRefinementsConnectorParamsItem,
    CurrentRefinementsConnectorParamsRefinement,
} from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';

const getPromotions = async (
    productId: string,
    signal: AbortSignal
): Promise<Nullable<MultiProductPromotionResponse>> =>
    await typedFetch<MultiProductPromotionResponse>(
        getNextApiRoute(`promotions?ids=${productId}`),
        {
            headers: {
                accept: 'application/json',
                'content-type': 'application/json',
            },
            method: HttpMethod.GET,
            signal,
        }
    );

export const updateProductsWithPromotions = (
    product: Product,
    promotionsToUse: MultiProductPromotionResponseItem[],
    isAuthenticated: boolean
): Product => {
    const productPromotions = promotionsToUse.find(
        (promotionList: MultiProductPromotionResponseItem) =>
            +promotionList.id === product.masterProductID
    );
    if (
        productPromotions &&
        productPromotions.promotions &&
        productPromotions.promotions.length > 0
    ) {
        return {
            ...product,
            promos: productPromotions.promotions
                .filter(promo => isUserEligibleForPromo(promo, isAuthenticated))
                .map((promo: ProductPromotion) => ({
                    title: promo.callout_msg,
                    description: '',
                })),
        };
    }

    return product;
};

export const getProductsPromotions = async (
    products: Product[],
    setProductsAndPromotions: (products: Product[]) => void,
    signal: AbortSignal,
    signal2: AbortSignal,
    isAuthenticated: boolean
) => {
    // need to split into two calls, API only supports a limited amount of productIds at a time
    const productList1 = products
        .slice(0, 23)
        .map(p => p.masterProductID)
        .join(',');
    const productList2 = products
        .slice(23)
        .map(p => p.masterProductID)
        .join(',');

    const promises: Promise<Nullable<MultiProductPromotionResponse>>[] = [];
    promises.push(getPromotions(productList1, signal));
    if (productList2) {
        promises.push(getPromotions(productList2, signal2));
    }

    const response = await Promise.all(promises);

    let allPromotions: MultiProductPromotionResponseItem[] = [];
    response.forEach((r: Nullable<MultiProductPromotionResponse>) => {
        if (r && r.success && r.products.length > 0) {
            allPromotions = allPromotions.concat(r.products);
        }
    });
    const allProducts: Product[] = products.map((p: Product) =>
        updateProductsWithPromotions(p, allPromotions, isAuthenticated)
    );
    setProductsAndPromotions(allProducts);
};

export const pushEvent = (
    productsToSend: Product[],
    isSearchPage: boolean,
    currentCategories: CurrentRefinementsConnectorParamsItem,
    searchWord?: string
) => {
    const categories = currentCategories
        ? currentCategories.refinements.map(
              (c: CurrentRefinementsConnectorParamsRefinement) =>
                  c.value.toString()
          )
        : [];
    const splitCategories =
        categories.length > 0 ? categories[0].split(' > ') : [];

    if (productsToSend.length > 0) {
        dataLayerPush({
            event: Events.VIEW_ITEM_LIST,
            ...createItemsViewEvent(
                productsToSend,
                isSearchPage ? 'search' : 'main navigation',
                isSearchPage ? 'search' : 'standard plp',
                splitCategories,
                searchWord ?? undefined
            ),
        });

        const list = isSearchPage ? 'Search Results' : 'PLP Page';
        trackEEImpression({
            products: productsToSend.map(({ rawProductHit }) => rawProductHit),
            list,
        });
    }
};

export const stringifyFilters = (
    filters: CurrentRefinementsConnectorParamsItem[]
) =>
    filters
        .map(
            (r: CurrentRefinementsConnectorParamsItem) =>
                `${r.attribute}${r.refinements.map(
                    (f: CurrentRefinementsConnectorParamsRefinement) => f.value
                )}`
        )
        .toString();

export const slugToTitle = (slug?: string) => {
    if (!slug) {
        return '';
    }

    let titleArray =
        slug.split('/')?.pop()?.replaceAll('-', ' ').split(' ') ?? [];

    const indexOfAnd = titleArray?.indexOf('and');
    const count = titleArray.reduce(
        (acc, val) => (val === 'and' ? acc + 1 : acc),
        0
    );
    if (indexOfAnd >= 2 && count < 2) {
        titleArray = titleArray.map((word, index) => {
            if (indexOfAnd - index >= 2) {
                return word.concat(',');
            }
            return word;
        });
    }
    return titleArray.join(' ');
};
